import Dialog from '#lib/components/dialog.jsx';
import Select from '#lib/components/select.jsx';
import { useEffect, useRef, useState } from 'react';
import FeedbackCard from './feedback-card.jsx';
import { defaultMakeReactKey } from '#helpers/helpers.js';
import { useFeedbacks } from './use-feedbacks.js';

export default function FeedbackModal(props) {
    const { isOpen, onClose, feedbacks, itineraryId } = props;

    const allRatingLabel = 'allRatings';
    const allTypesLabel = 'allTypes';
    const [currentType, setCurrentType] = useState(allTypesLabel);
    const [currentSort, setCurrentSort] = useState(null);
    const [rating, setRating] = useState(allRatingLabel);
    const allRatings = Array.from({ length: 5 }, (_, i) => i + 1).concat(_t.edit.feedbacks.allRatings());

    const typeOptions = [_t.edit.feedbacks.all(), _t.edit.feedbacks.itinerary(), _t.edit.feedbacks.plans()];
    const sortOptions = [
        _t.edit.feedbacks.datePlus(),
        _t.edit.feedbacks.dateMinus(),
        _t.edit.feedbacks.ratingPlus(),
        _t.edit.feedbacks.ratingMinus()
    ];

    const orderedFeedbacks = feedbacks?.sort((a, b) => {
        if (currentSort === sortOptions[0]) return new Date(a.created) - new Date(b.created);
        else if (currentSort === sortOptions[1]) return new Date(b.created) - new Date(a.created);
        else if (currentSort === sortOptions[2]) return b.rating - a.rating;
        else if (currentSort === sortOptions[3]) return a.rating - b.rating;
    });

    const typedFeedbacks = orderedFeedbacks?.filter((fb) => {
        if (currentType === allTypesLabel) return true;
        return currentType === typeOptions[1] ? !fb.planName : fb.planName;
    });

    const ratedFeedbacks = typedFeedbacks?.filter((fb) => {
        if (rating === allRatingLabel) return true;
        return fb.rating == rating;
    });

    const containerRef = useRef(null);
    const checkFeedback = useFeedbacks();
    const handleCheckFeedback = (id) => {
        checkFeedback.mutate({ id, itineraryId });
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const feedback = ratedFeedbacks.find((fb) => fb.id == entry.target.id);
                    if (entry.isIntersecting && !feedback?.checked) {
                        handleCheckFeedback(feedback.id);
                    }
                });
            },
            { threshold: 0.8 }
        );

        const divs = containerRef.current?.querySelectorAll('.feedback');
        divs?.forEach((div) => observer.observe(div));

        return () => observer.disconnect();
    }, [containerRef]);

    return (
        <Dialog isOpen={isOpen} onClose={onClose}>
            <Dialog.Panel className="flex flex-col space-y-4 w-full min-w-[300px] max-w-[700px] max-h-[80%]">
                <div className="pr-4">
                    <Dialog.CloseButton />
                    <p className="text-2xl">{_t.edit.feedbacks.feedbacks()}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                    <Select.Full
                        label={_t.edit.feedbacks.feedbackType()}
                        options={typeOptions}
                        onChange={(v) => {
                            if (v === _t.edit.feedbacks.all()) setCurrentType(allTypesLabel);
                            else setCurrentType(v);
                        }}
                        inPortal={false}
                        makeReactKey={defaultMakeReactKey}
                        value={currentType === allTypesLabel ? _t.edit.feedbacks.all() : currentType}
                    />
                    <Select.Full
                        label={_t.edit.feedbacks.feedbackOrder()}
                        options={sortOptions}
                        onChange={(v) => setCurrentSort(v)}
                        inPortal={false}
                        makeReactKey={defaultMakeReactKey}
                        value={currentSort}
                    />
                    <Select.Full
                        label={_t.edit.feedbacks.feedbackRating()}
                        options={allRatings}
                        onChange={(v) => {
                            if (isNaN(v)) setRating(allRatingLabel);
                            else setRating(v);
                        }}
                        inPortal={false}
                        makeReactKey={defaultMakeReactKey}
                        value={rating === allRatingLabel ? _t.edit.feedbacks.allRatings() : rating}
                    />
                </div>
                <div ref={containerRef} className="flex flex-col space-2 w-full gap-4 h-full overflow-y-auto">
                    {ratedFeedbacks && ratedFeedbacks.length > 0 ? (
                        <>
                            {ratedFeedbacks?.map((fb) => {
                                return <FeedbackCard key={fb?.id} {...fb} />;
                            })}
                        </>
                    ) : (
                        <div className="w-full p-4 bg-neutral-100 text-neutral-600 flex items-center justify-center">
                            <p>{_t.edit.feedbacks.noFeedbacks()}</p>
                        </div>
                    )}
                </div>
            </Dialog.Panel>
        </Dialog>
    );
}
